import { AlertTriangle, CheckCircle, Info, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type AlertVariant = "error" | "warning" | "success" | "info";

interface AlertMessageProps {
  variant: AlertVariant;
  message: string;
  className?: string;
}

const variantStyles: Record<
  AlertVariant,
  { bg: string; border: string; text: string; Icon: typeof AlertTriangle }
> = {
  error: {
    bg: "bg-[var(--color-error-light)]",
    border: "border-[var(--color-error)]/30",
    text: "text-[var(--color-error)]",
    Icon: XCircle,
  },
  warning: {
    bg: "bg-[var(--color-warning-light)]",
    border: "border-[var(--color-warning)]/30",
    text: "text-[var(--color-warning)]",
    Icon: AlertTriangle,
  },
  success: {
    bg: "bg-[var(--color-success-light)]",
    border: "border-[var(--color-success)]/30",
    text: "text-[var(--color-success)]",
    Icon: CheckCircle,
  },
  info: {
    bg: "bg-[var(--color-info-light)]",
    border: "border-[var(--color-info)]/30",
    text: "text-[var(--color-info)]",
    Icon: Info,
  },
};

export function AlertMessage({ variant, message, className }: AlertMessageProps) {
  const { bg, border, text, Icon } = variantStyles[variant];

  return (
    <div
      className={cn("flex items-start gap-3 rounded-lg border px-4 py-3", bg, border, className)}
      role="alert"
      data-testid="alert-message"
    >
      <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", text)} />
      <p className={cn("text-sm font-medium", text)}>{message}</p>
    </div>
  );
}
